import * as React from "react";
import {
  Button,
  Checkbox,
  Input,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Text,
  makeStyles,
  type ButtonProps,
  type PopoverProps,
} from "@fluentui/react-components";

const useStyles = makeStyles({
  surface: {
    minWidth: "300px",
    maxWidth: "520px",
    display: "grid",
    gap: "8px",
  },
  list: {
    display: "grid",
    gap: "6px",
    maxHeight: "320px",
    overflowY: "auto",
    paddingRight: "2px",
  },
  optionButton: {
    justifyContent: "flex-start",
    width: "100%",
  },
  empty: {
    color: "var(--colorNeutralForeground3)",
  },
  search: {
    width: "100%",
  },
});

export interface SelectionPopoverOption {
  key: string;
  label: string;
  selected: boolean;
  disabled?: boolean;
}

export interface SelectionPopoverControlProps {
  buttonLabel: string;
  buttonIcon?: JSX.Element;
  buttonAriaLabel?: string;
  options: SelectionPopoverOption[];
  onToggleOption: (optionKey: string, checked: boolean) => void;
  title?: string;
  emptyMessage?: string;
  multiselect?: boolean;
  buttonAppearance?: ButtonProps["appearance"];
  enableSearch?: boolean;
  searchPlaceholder?: string;
}

export function SelectionPopoverControl({
  buttonLabel,
  buttonIcon,
  buttonAriaLabel,
  options,
  onToggleOption,
  title,
  emptyMessage = "No options available.",
  multiselect = true,
  buttonAppearance = "secondary",
  enableSearch = true,
  searchPlaceholder = "Filter options",
}: SelectionPopoverControlProps): JSX.Element {
  const styles = useStyles();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const filteredOptions = React.useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) {
      return options;
    }

    return options.filter((option) => option.label.toLowerCase().includes(trimmed));
  }, [options, query]);

  const onOpenChange: PopoverProps["onOpenChange"] = (_event, data) => {
    setOpen(data.open);
    if (!data.open) {
      setQuery("");
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange} positioning="below-start">
      <PopoverTrigger disableButtonEnhancement>
        <Button appearance={buttonAppearance} icon={buttonIcon} aria-label={buttonAriaLabel} title={buttonAriaLabel || buttonLabel}>
          {buttonLabel}
        </Button>
      </PopoverTrigger>

      <PopoverSurface className={styles.surface}>
        {title ? <Text weight="semibold">{title}</Text> : null}

        {enableSearch && options.length > 8 ? (
          <Input
            className={styles.search}
            value={query}
            onChange={(_event, data) => setQuery(data.value)}
            placeholder={searchPlaceholder}
          />
        ) : null}

        {filteredOptions.length > 0 ? (
          <div className={styles.list}>
            {filteredOptions.map((option) =>
              multiselect ? (
                <Checkbox
                  key={option.key}
                  label={option.label}
                  checked={option.selected}
                  disabled={option.disabled}
                  onChange={(_event, data) => onToggleOption(option.key, Boolean(data.checked))}
                />
              ) : (
                <Button
                  key={option.key}
                  className={styles.optionButton}
                  appearance="subtle"
                  disabled={option.disabled}
                  onClick={() => {
                    onToggleOption(option.key, true);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </Button>
              ),
            )}
          </div>
        ) : (
          <Text className={styles.empty}>{query ? "No options match your filter." : emptyMessage}</Text>
        )}
      </PopoverSurface>
    </Popover>
  );
}